import { Application, Container, type Renderer } from 'pixi.js';
import type { ViewportInfo } from './Viewport';

export type QualityLevel = 'high' | 'low';

export interface RenderCaps {
  renderer: 'webgl' | 'webgpu' | 'canvas';
  quality: QualityLevel;
  maxTextureSize: number;
}

/**
 * Owns the one and only PixiJS Application. It never auto-renders: the
 * GameLoop calls render() so there is exactly one animation loop.
 */
export class PixiHost {
  readonly app = new Application();
  readonly stage = new Container();
  caps: RenderCaps = { renderer: 'webgl', quality: 'high', maxTextureSize: 2048 };
  /** Draw calls issued during the last rendered frame (WebGL only; -1 if unknown). */
  drawCalls = -1;
  private drawCounter = 0;
  /** Dynamic resolution: 1 = full, lower while the frame rate is struggling (AdaptiveQuality). */
  private renderScale = 1;
  private lastVp: ViewportInfo | null = null;

  /**
   * mode 'cpu' draws with Canvas 2D first (light on the graphics card), 'gpu'
   * with WebGL first; either falls back to the other. Never asks for the
   * high-performance GPU: on laptops that wakes the power-hungry one.
   */
  async init(canvas: HTMLCanvasElement, vp: ViewportInfo, mode: 'cpu' | 'gpu' = 'cpu'): Promise<void> {
    const lowEnd = isLowEndDevice();
    const light = lowEnd || mode === 'cpu';
    await this.app.init({
      canvas,
      width: vp.width,
      height: vp.height,
      resolution: light ? Math.min(vp.dpr, 1.5) : vp.dpr,
      autoDensity: true,
      backgroundAlpha: 0,
      antialias: !light,
      autoStart: false,
      sharedTicker: false,
      preference: mode === 'cpu' ? ['canvas', 'webgl'] : ['webgl', 'canvas'],
      powerPreference: 'low-power',
      hello: false,
    });
    this.app.ticker.stop();
    this.app.stage.addChild(this.stage);
    // The canvas never takes input; DOM handles UI.
    this.app.stage.eventMode = 'none';

    const name = this.app.renderer.name as RenderCaps['renderer'];
    this.caps = {
      renderer: name,
      quality: lowEnd || name === 'canvas' ? 'low' : 'high',
      maxTextureSize: this.readMaxTexture(),
    };
    this.instrumentDrawCalls();
    this.lastVp = vp;
  }

  get renderer(): Renderer {
    return this.app.renderer;
  }

  resize(vp: ViewportInfo): void {
    this.lastVp = vp;
    const base = this.caps.quality === 'low' ? Math.min(vp.dpr, 1.5) : vp.dpr;
    const res = Math.max(0.5, base * this.renderScale);
    if (this.app.renderer.resolution !== res) this.app.renderer.resolution = res;
    this.app.renderer.resize(vp.width, vp.height);
  }

  /** Renders at a fraction of the normal resolution (the canvas is stretched back to full size). */
  setRenderScale(scale: number): void {
    if (scale === this.renderScale) return;
    this.renderScale = scale;
    if (this.lastVp) this.resize(this.lastVp);
  }

  get currentRenderScale(): number {
    return this.renderScale;
  }

  render(): void {
    this.drawCounter = 0;
    this.app.render();
    if (this.drawCalls !== -1 || this.drawCounter > 0) this.drawCalls = this.drawCounter;
  }

  /** Count active display objects in the scene graph. */
  countObjects(): number {
    let n = 0;
    const walk = (c: Container): void => {
      n++;
      for (const child of c.children) walk(child);
    };
    walk(this.app.stage);
    return n;
  }

  private readMaxTexture(): number {
    const gl = (this.app.renderer as unknown as { gl?: WebGLRenderingContext }).gl;
    return gl ? (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) : 2048;
  }

  /** Wrap the GL draw entry points to measure real draw calls per frame. */
  private instrumentDrawCalls(): void {
    const gl = (this.app.renderer as unknown as { gl?: WebGL2RenderingContext }).gl;
    if (!gl) return;
    this.drawCalls = 0;
    const names = ['drawElements', 'drawArrays', 'drawElementsInstanced', 'drawArraysInstanced'] as const;
    for (const name of names) {
      const original = gl[name] as ((...args: unknown[]) => void) | undefined;
      if (typeof original !== 'function') continue;
      const bound = original.bind(gl);
      (gl as unknown as Record<string, unknown>)[name] = (...args: unknown[]) => {
        this.drawCounter++;
        bound(...args);
      };
    }
  }
}

function isLowEndDevice(): boolean {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const cores = nav.hardwareConcurrency ?? 8;
  const mem = nav.deviceMemory ?? 8;
  return cores <= 2 || mem <= 2;
}
